import { App } from "antd";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { InferType } from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { setAuthenticationState } from "@/redux/authentication/reducer";
import { LocalStorageName } from "@/redux/api";
import { handleErrors } from "@/shared/utils/forms.js";
import { usePatchUserByIdMutation } from "@/redux/service";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { HookFormInput } from "@/components_v2/FormFields/ReactHookForm/fields";
import EmailIcon from "@/assets/icons/email.svg?react";
import { ChangeAccountFormProps } from "@/features/users/settings/types";

import "../styles.css";

const ChangeEmailForm = ({ userId }: ChangeAccountFormProps) => {
  const { notification } = App.useApp();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [updateUser, { isLoading }] = usePatchUserByIdMutation();

  const changeEmailSchema = yup.object().shape({
    newEmail: yup
      .string()
      .typeError(t("users.validation.emailNotValid"))
      .email(t("users.validation.emailNotValid"))
      .required(t("users.validation.emailRequired")),
    password: yup
      .string()
      .typeError(t("users.validation.passwordNotValid"))
      .min(8, t("users.validation.passwordLengthValidation"))
      .matches(/[A-Z]/, t("users.validation.passwordCapitalLetterValidation"))
      .matches(/[0-9]/, t("users.validation.passwordNumberValidation"))
      .required(t("users.validation.passwordRequired")),
  });

  const form = useForm({
    resolver: yupResolver(changeEmailSchema),
    mode: "onTouched",
  });

  const onSubmit = (values: InferType<typeof changeEmailSchema>) => {
    updateUser({ id: userId, data: values })
      .unwrap()
      .then(() => {
        notification.success({
          message: t("users.labels.emailChanged"),
        });
        dispatch(setAuthenticationState({ isAuthenticated: false }));
        window.localStorage.removeItem(LocalStorageName);
        navigate("/login");
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  return (
    <div className="user-settings-card flex flex-col gap-300">
      <div className="flex items-center gap-200">
        <BaseSvgRenderer component={EmailIcon} width="20" height="20" />
        <h3 className="header-m-medium">{t("users.labels.changeEmail")}</h3>
      </div>

      <FormFieldWrapper label={t("users.labels.password")}>
        <HookFormInput name="password" type="password" control={form.control} />
      </FormFieldWrapper>
      <FormFieldWrapper label={t("users.labels.newEmail")}>
        <HookFormInput name="newEmail" type="text" control={form.control} />
      </FormFieldWrapper>

      <BaseButton
        className="self-start"
        disabled={isLoading}
        onClick={form.handleSubmit(onSubmit)}
      >
        {t("users.labels.updateEmail")}
      </BaseButton>
    </div>
  );
};

export default ChangeEmailForm;
